import React, { useState } from "react";
import { useQuery } from "@apollo/client/react";
import moment from "moment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import ConfirmDeleteModal from "../../components/images/ConfirmDeleteModal";
import useMutation from "../../hooks/useMutation";
import { usePermissions } from "../../contexts/permissions";
import ItemId from "../../views/shopping/Orders/ItemId";
import { PROJECT_IN_CART_QUANTITY_DATA_QUERY } from "../../constants/graphql/queries";
import {
  REMOVE_ASSET_IN_CART_QUANTITY_MUTATION,
  RELEASE_PROJECT_SHOPPING_CART_MUTATION,
} from "../../constants/graphql/mutations";

const formatUser = user => user.name || user.email || "Guest User";

const formatAddedAt = addedAt => (
  <>
    {moment(addedAt).format("MM/DD/YYYY")}
    <br />
    {moment(addedAt).format("hh:mm A")}
  </>
);

const imageForAsset = asset => asset.images?.find(image => image.default)?.url || asset.images?.[0]?.url;

export default function ProjectInCartQuantityModal({ projectId, onClose }) {
  const [entryToRemove, setEntryToRemove] = useState(null);
  const [userToRelease, setUserToRelease] = useState(null);
  const { can } = usePermissions();
  const { data, loading, error, refetch } = useQuery(PROJECT_IN_CART_QUANTITY_DATA_QUERY, {
    variables: { projectId },
    skip: !projectId,
    fetchPolicy: "network-only",
  });

  const [removeAssetInCartQuantity, { loading: removing }] = useMutation(
    REMOVE_ASSET_IN_CART_QUANTITY_MUTATION,
    {
      onSuccess: () => {
        refetch();
        setEntryToRemove(null);
      },
      onFailure: () => setEntryToRemove(null),
    }
  );
  const [releaseProjectShoppingCart, { loading: releasingCart }] = useMutation(
    RELEASE_PROJECT_SHOPPING_CART_MUTATION,
    {
      onSuccess: () => {
        refetch();
        setUserToRelease(null);
      },
      onFailure: () => setUserToRelease(null),
    }
  );

  const entries = data?.projectInCartQuantityData || [];
  const entriesByUser = entries.reduce((groups, entry) => {
    const userId = entry.user.id;
    groups[userId] ||= { user: entry.user, entries: [] };
    groups[userId].entries.push(entry);
    return groups;
  }, {});

  const removeEntry = () => {
    if (!entryToRemove) return;

    removeAssetInCartQuantity({
      variables: {
        assetId: entryToRemove.asset.id,
        userId: entryToRemove.user.id,
      },
    });
  };

  const releaseUserCart = () => {
    if (!userToRelease) return;

    releaseProjectShoppingCart({
      variables: {
        projectId,
        userId: userToRelease.id,
      },
    });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth aria-labelledby="project-in-cart-quantity-modal">
      <DialogTitle id="project-in-cart-quantity-modal">
        <Box display="flex" alignItems="center" justifyContent="center" position="relative" width="100%">
          <Typography color="textPrimary" fontSize={24} fontWeight={500}>
            Items in Shopping Carts
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            size="small"
            sx={{ position: "absolute", right: 0, top: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">Failed to load shopping-cart items. Please try again.</Alert>
        ) : entries.length === 0 ? (
          <Typography color="text.secondary" align="center" py={4}>
            No assets in this project are currently in users&apos; shopping carts.
          </Typography>
        ) : (
          Object.values(entriesByUser).map(({ user, entries: userEntries }) => (
            <Box key={user.id} mb={3}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography fontWeight={500}>{formatUser(user)}</Typography>
                {can("assets.write") && (
                  <Button
                    color="error"
                    disabled={releasingCart}
                    onClick={() => setUserToRelease(user)}
                    size="small"
                  >
                    Release All Items
                  </Button>
                )}
              </Box>
              <Table size="small" aria-label={`Shopping cart items for ${formatUser(user)}`}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 80 }} align="center">Image</TableCell>
                    <TableCell align="center">Item ID</TableCell>
                    <TableCell align="center">Description</TableCell>
                    {userEntries.some(entry => entry.asset.model) && <TableCell align="center">Model</TableCell>}
                    <TableCell align="center">Added to Cart</TableCell>
                    <TableCell align="center">Quantity</TableCell>
                    {can("assets.write") && <TableCell align="center" />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userEntries.map(entry => {
                    const imageUrl = imageForAsset(entry.asset);

                    return (
                      <TableRow key={`${entry.asset.id}-${entry.user.id}`}>
                        <TableCell align="center">
                          {imageUrl ? (
                            <img
                              style={{ maxWidth: 90, maxHeight: 60 }}
                              src={imageUrl}
                              alt={entry.asset.description || entry.asset.itemId}
                            />
                          ) : (
                            "No image"
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <ItemId asset={entry.asset} />
                        </TableCell>
                        <TableCell align="center">{entry.asset.description}</TableCell>
                        {userEntries.some(item => item.asset.model) && (
                          <TableCell align="center">{entry.asset.model}</TableCell>
                        )}
                        <TableCell align="center">{formatAddedAt(entry.addedAt)}</TableCell>
                        <TableCell align="center">{entry.quantity}</TableCell>
                        {can("assets.write") && (
                          <TableCell align="center">
                            <Tooltip arrow title="Release Asset">
                              <span>
                                <IconButton
                                  color="inherit"
                                  disabled={removing}
                                  onClick={() => setEntryToRemove(entry)}
                                  size="small"
                                >
                                  <HighlightOffIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Box display="flex" flexGrow={1} justifyContent="flex-end" py={1} px={2.5}>
          <Button variant="outlined" onClick={onClose} color="primary">
            Close
          </Button>
        </Box>
      </DialogActions>
      {entryToRemove && (
        <ConfirmDeleteModal
          title="Release Asset"
          descr={`Release ${entryToRemove.asset.itemId} from ${formatUser(entryToRemove.user)}'s shopping cart?`}
          onClose={() => setEntryToRemove(null)}
          onDelete={removeEntry}
        />
      )}
      {userToRelease && (
        <ConfirmDeleteModal
          title="Release All Items"
          descr={`Release all items in ${formatUser(userToRelease)}'s shopping cart for this project?`}
          onClose={() => setUserToRelease(null)}
          onDelete={releaseUserCart}
        />
      )}
    </Dialog>
  );
}
